// Badge colorimétrique d'un score de confiance OCR (0-100).
//
// Seuils :
//   vert  : >= 85      → champ quasi certain, pré-validé
//   ambre : 70 - 84    → à vérifier par l'utilisateur
//   rouge : < 70 ou null → probablement faux, édition impérative
//
// Aligné sur la convention utilisée dans la maquette du Lot 0 et sur la
// logique backend (`settings.ocr_fallback_threshold` = 70).

type Props = {
  /** Score 0-100, ou null si le champ n'a pas été extrait. */
  value: number | null;
  /** Si true, affiche juste la pastille sans texte ("●"). */
  compact?: boolean;
};

export function confidenceBucket(value: number | null): "green" | "amber" | "red" {
  if (value === null || value < 70) return "red";
  if (value < 85) return "amber";
  return "green";
}

const BUCKET_CLASSES: Record<"green" | "amber" | "red", string> = {
  green: "bg-emerald-100 text-emerald-700 border-emerald-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  red: "bg-red-100 text-red-700 border-red-200",
};

const BUCKET_LABELS: Record<"green" | "amber" | "red", string> = {
  green: "Vérifié",
  amber: "À vérifier",
  red: "Incertain",
};

export function ConfidenceBadge({ value, compact = false }: Props) {
  const bucket = confidenceBucket(value);
  const label = BUCKET_LABELS[bucket];
  const display = value === null ? "—" : `${Math.round(value)}%`;
  return (
    <span
      className={[
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
        BUCKET_CLASSES[bucket],
      ].join(" ")}
      role="status"
      aria-label={`Confiance : ${label}, ${display}`}
      title={`${label} (${display})`}
    >
      <span aria-hidden="true">●</span>
      {!compact && <span>{display}</span>}
    </span>
  );
}

/** Classe CSS pour un champ d'input selon son niveau de confiance. */
export function inputRingForConfidence(value: number | null): string {
  switch (confidenceBucket(value)) {
    case "green":
      return "ring-1 ring-emerald-300 focus:ring-emerald-400";
    case "amber":
      return "ring-1 ring-amber-300 focus:ring-amber-500";
    case "red":
      return "ring-2 ring-red-300 focus:ring-red-500";
  }
}
